import { useState } from 'react';
import {
  Plus, Search, Filter, Briefcase, Clock, TrendingUp, Users,
  Star, ChevronRight, MoreHorizontal, Video, FileText, DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const JOB_POSTINGS = [
  { id: 1, title: 'Senior Software Engineer', type: 'Full-time', location: 'Hybrid', salary: '₹18-24 LPA', applications: 47, status: 'active', skills: ['React', 'Node.js', 'AWS'] },
  { id: 2, title: 'Product Manager', type: 'Full-time', location: 'Remote', salary: '₹15-20 LPA', applications: 62, status: 'active', skills: ['Product Strategy', 'Analytics', 'Agile'] },
  { id: 3, title: 'ML Engineer', type: 'Full-time', location: 'On-site', salary: '₹20-30 LPA', applications: 38, status: 'draft', skills: ['Python', 'PyTorch', 'MLOps'] },
];

const CANDIDATES = [
  { id: 1, name: 'Vikas Nair', role: 'Senior SWE', exp: '5 yrs', skills: ['React', 'TypeScript', 'AWS'], score: 91, location: 'Bangalore', status: 'shortlisted' },
  { id: 2, name: 'Deepa Krishnan', role: 'Product Manager', exp: '6 yrs', skills: ['Product', 'Analytics', 'Figma'], score: 88, location: 'Hyderabad', status: 'interview' },
  { id: 3, name: 'Suresh Mishra', role: 'ML Engineer', exp: '4 yrs', skills: ['PyTorch', 'Python', 'MLOps'], score: 94, location: 'Patna', status: 'offered' },
  { id: 4, name: 'Nidhi Agarwal', role: 'Senior SWE', exp: '4 yrs', skills: ['Vue.js', 'Python', 'Docker'], score: 85, location: 'Pune', status: 'applied' },
];

const SALARY_BENCHMARKS = [
  { role: 'Software Engineer', min: '₹10 LPA', avg: '₹16 LPA', max: '₹30 LPA' },
  { role: 'Product Manager', min: '₹12 LPA', avg: '₹20 LPA', max: '₹40 LPA' },
  { role: 'ML Engineer', min: '₹14 LPA', avg: '₹22 LPA', max: '₹45 LPA' },
  { role: 'Data Scientist', min: '₹10 LPA', avg: '₹18 LPA', max: '₹35 LPA' },
];

export default function JobHiring() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const statusConfig = {
    applied: { label: 'Applied', cls: 'badge-muted' },
    shortlisted: { label: 'Shortlisted', cls: 'badge-info' },
    interview: { label: 'Interview', cls: 'badge-warning' },
    offered: { label: 'Offered', cls: 'badge-success' },
  };

  const handlePost = () => {
    toast.success('Job posted successfully!');
    setOpen(false);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Job Hiring</h1>
          <p className="text-muted-foreground text-sm mt-1">Post full-time roles, manage candidates, and issue offer letters</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gradient-primary text-primary-foreground border-0 gap-2">
              <Plus className="w-4 h-4" /> Post Job
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Post New Job</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div><Label>Job Title</Label><Input placeholder="e.g. Senior Software Engineer" className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Job type" /></SelectTrigger>
                    <SelectContent>
                      {['Full-time', 'Part-time', 'Contract', 'Remote'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Location</Label>
                  <Select>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Location" /></SelectTrigger>
                    <SelectContent>
                      {['Remote', 'On-site', 'Hybrid'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Salary Range (LPA)</Label><Input placeholder="e.g. 15-25 LPA" className="mt-1" /></div>
              <div><Label>Job Description</Label><Textarea placeholder="Responsibilities, requirements, benefits..." className="mt-1" rows={3} /></div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="gradient-primary text-primary-foreground border-0" onClick={handlePost}>Post Job</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="postings">
        <TabsList className="bg-muted">
          <TabsTrigger value="postings">Job Postings</TabsTrigger>
          <TabsTrigger value="candidates">Candidate Pool</TabsTrigger>
          <TabsTrigger value="salary">Salary Benchmarking</TabsTrigger>
          <TabsTrigger value="offers">Offer Letters</TabsTrigger>
        </TabsList>

        <TabsContent value="postings" className="mt-6 space-y-4">
          {JOB_POSTINGS.map(job => (
            <Card key={job.id} className="border-border hover:shadow-card transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-foreground">{job.title}</h3>
                      <span className={job.status === 'active' ? 'badge-success' : 'badge-muted'}>{job.status}</span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-3">
                      <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{job.type}</span>
                      <span className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" />{job.location}</span>
                      <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" />{job.salary}</span>
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{job.applications} applications</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {job.skills.map(s => <span key={s} className="badge-primary">{s}</span>)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="border-border gap-1"><Video className="w-3.5 h-3.5" />Interview Room</Button>
                    <Button variant="outline" size="sm" className="border-border">Edit</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="candidates" className="mt-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search candidates..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <Button variant="outline" size="sm" className="border-border gap-2">
              <Filter className="w-4 h-4" /> Filter
            </Button>
          </div>
          <div className="space-y-3">
            {CANDIDATES.map(c => (
              <Card key={c.id} className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-xs font-bold text-primary-foreground flex-shrink-0">
                      {c.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-medium text-foreground">{c.name}</span>
                        <span className={statusConfig[c.status].cls}>{statusConfig[c.status].label}</span>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                        <span>{c.role}</span>
                        <span>{c.exp} experience</span>
                        <span>{c.location}</span>
                      </div>
                      <div className="flex gap-1.5 mt-1.5">
                        {c.skills.map(s => <span key={s} className="badge-muted">{s}</span>)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-primary">{c.score}%</div>
                      <div className="text-xs text-muted-foreground">Match Score</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="border-border"><Video className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" className="gradient-primary text-primary-foreground border-0">Issue Offer</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="salary" className="mt-6">
          <Card className="border-border">
            <CardHeader><CardTitle className="text-base">Salary Benchmarks — Bihar/India Market</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {SALARY_BENCHMARKS.map(row => (
                  <div key={row.role} className="p-4 rounded-xl bg-muted/50 border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-foreground text-sm">{row.role}</span>
                      <span className="badge-info">Domain Average: {row.avg}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div><span className="text-muted-foreground">Min: </span><span className="font-semibold text-foreground">{row.min}</span></div>
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full gradient-primary" style={{ width: '60%' }} />
                      </div>
                      <div><span className="text-muted-foreground">Max: </span><span className="font-semibold text-foreground">{row.max}</span></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-4 rounded-xl bg-warning/8 border border-warning/20">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-warning">Note:</span> Full salary breakdown available on Pro plan. Upgrade to access percentile data, role-specific filters, and custom date ranges.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="offers" className="mt-6">
          <div className="space-y-4">
            {CANDIDATES.filter(c => c.status === 'offered').map(c => (
              <Card key={c.id} className="border-border">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-9 h-9 text-primary" />
                      <div>
                        <p className="font-semibold text-foreground">Offer Letter — {c.name}</p>
                        <p className="text-xs text-muted-foreground">{c.role} • Generated today</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="border-border">Preview</Button>
                      <Button size="sm" className="gradient-primary text-primary-foreground border-0">Send Offer</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            <div className="text-center py-8 text-muted-foreground text-sm">No pending offer letters</div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
